import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import {
  NbMediaBreakpointsService,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
  NbToastrService,
} from '@nebular/theme';
import { LayoutService } from '../../../@core/utils';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TokenService } from '../../../common/services/token.service';
import { ProfileService } from '../../../common/services/profile.service';
import { Router } from '@angular/router';
import { HeaderNotificationsComponent } from './header-notifications/header-notifications.component';
import { HeaderSystemNotificationsComponent } from './header-system-notifications/header-system-notifications.component';
import { User } from '../../../pages/users/models/user.model';
import { SocketService } from '../../../common/services/socket.service';
import { NbDialogService } from '@nebular/theme';
import { CheckoutModalComponent } from '../../../pages/checkout/checkout-modal/checkout-modal.component';
import { TabNotificationSoundService } from '../../../common/services/tab-notification-sound.service';
import { AccountStorageResetService } from '../../../common/services/account-storage-reset.service';

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  providers: [HeaderNotificationsComponent, HeaderSystemNotificationsComponent],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly: boolean = false;
  user: User | null = null;
  isTabMuted = false;

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    {
      value: 'cosmic',
      name: 'Cosmic',
    },
    {
      value: 'corporate',
      name: 'Corporate',
    },
  ];

  isDark: boolean = false;
  toggleTheme() {
    this.isDark = !this.isDark;
    const theme = this.isDark ? 'dark' : 'default';
    localStorage.setItem('theme', theme);
    this.themeService.changeTheme(theme);
  }

  currentTheme = 'default';

  userMenu = [
    // { title: 'Profile', value: 'profile' },
    { title: 'پروفایل', value: 'profile' },
    { title: 'خروج از حساب', value: 'logout' },
  ];

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private tokenService: TokenService,
    private socketService: SocketService,
    private profileService: ProfileService,
    private router: Router,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private tabNotificationSound: TabNotificationSoundService,
    private accountStorageReset: AccountStorageResetService,
  ) {}

  async ngOnInit() {
    this.currentTheme = localStorage.getItem('theme') || 'default';
    this.isDark = this.currentTheme == 'dark' ? true : false;
    this.themeService.changeTheme(this.currentTheme);

    this.tabNotificationSound.muted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((muted) => (this.isTabMuted = muted));

    this.profileService
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => (this.user = value));

    const existing = this.tokenService.getUser<User>();
    if (existing) {
      this.profileService.setUser(existing);
    }

    await this.getUser();

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl),
      );

    this.themeService
      .onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      )
      .subscribe((themeName) => (this.currentTheme = themeName));
  }

  mobileHeaderMenuOpen = false;

  toggleMobileHeaderMenu() {
    this.mobileHeaderMenuOpen = !this.mobileHeaderMenuOpen;
  }

  toggleTabSound(): void {
    const muted = this.tabNotificationSound.toggle();
    this.toastrService.show(
      muted
        ? 'صدای اعلان‌های این تب قطع شد'
        : 'صدای اعلان‌های این تب فعال شد',
      muted ? 'حالت بی‌صدا' : 'حالت باصدا',
      { status: muted ? 'basic' : 'success', duration: 1800 },
    );
  }

  closeMobileHeaderMenu() {
    this.mobileHeaderMenuOpen = false;
  }

  private async getUser() {
    this.profileService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.data) {
            this.profileService.setUser(res.data);
          }
        },
        error: () => {},
      });
  }

  openCheckout() {
    this.dialogService
      .open(CheckoutModalComponent, {
        closeOnBackdropClick: true,
        dialogClass: 'checkout-modal-lg',
        hasScroll: true,
      })
      .onClose.subscribe((res) => {
        if (res) {
          this.getUser();
        }
      });
  }

  isRefreshing = false;

  handleRefresh(): void {
    this.isRefreshing = true;
    this.getUser();

    setTimeout(() => {
      this.isRefreshing = false;
    }, 800);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  hasRole(roleName: string): boolean {
    return (this.user?.roles || []).some((r) => r.name === roleName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(false, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.router.navigate(['/pages/dashboard']);
    return false;
  }

  ngAfterViewInit() {
    this.menuService
      .onItemClick()
      .pipe(
        takeUntil(this.destroy$),
        filter((ctx: any) => !!ctx?.item?.title),
      )
      .subscribe(async (ctx: any) => {
        const title = (ctx.item.title || '').toLowerCase();
        const value = (ctx.item.value || '').toLowerCase();
        if (value.includes('profile')) {
          this.router.navigate(['/pages/profile']);
          return;
        }
        if (value.includes('logout')) {
          this.socketService.disconnect();
          await this.accountStorageReset.resetAccountData();
          await this.router.navigate(['/login']);
        }
      });
  }
}
